import { isMapStyleReady } from '../mapcore.js';
// Layer fulmini Blitzortung v2 — port fedele della v1 (map-layers.js):
// stessi dati (lightning/api/app.php, provider=blitzortung), stessa resa canvas
// di WSBlitzCanvasLayer._drawStrike (aura radiale + nucleo + saetta 32×32),
// stesse fasce d'età (blitzAgeStyle), stessa logica finestre LIVE/PASSATO di
// renderBlitzWindow/blitzShowAtTime, stesso suono WebAudio e stesso pulse CSS
// "onda d'urto" sui fulmini nuovi (qui un maplibregl.Marker non interattivo).
//
// Architettura GL come stations.js: sprite canvas condivisi per combinazione
// `${size}|${color}|${glow}|${stroke}` → map.addImage(pixelRatio) → symbol
// layer con 'icon-image':['get','sk'] e opacità per-feature 'icon-opacity':
// ['get','op'] (quantizzata a passi 0.05 per massimizzare il riuso dei valori).

import { parseObservationTime } from '../lib/wsnow-contract.js';
import { NowcastTimeline } from '../ui/nowcast-timeline.js';

const SRC = 'ws-blitz';
const LYR = 'ws-blitz';

// --- costanti portate 1:1 dalla v1 (map-layers.js) ---------------------------
const WS_BLITZ_MAX_AGE_MIN = 120;            // profondità fetch (since_minutes)
const WS_BLITZ_WINDOW_MIN = 5;              // finestra mostrata per "scatto" passato
const WS_BLITZ_LIVE_EPSILON_MS = 90 * 1000; // T entro 90s da ORA = modalità live
const WS_BLITZ_LIVE_FULL_MIN = 25;          // live: fino a 25′ opacità/colore dalle fasce d'età
const WS_BLITZ_LIVE_FADE_MIN = 30;          // live: ultimi 30′ (25→30′ sbiaditi, poi spariscono)
const WS_BLITZ_REFRESH_MS = 12 * 1000;      // auto-refresh 12 s
// Dalla v2 (che vive in /v2/) il path della v1 'lightning/api/app.php' deve
// essere ASSOLUTO, altrimenti risolverebbe /v2/lightning/...
const WS_LIGHTNING_API = '/lightning/api/app.php';

export const state = {
    area: 'italy',
    fetchedAtMs: null,
    feedTimeMs: null,
    feedAgeSeconds: null,
    upstreamStale: false,
    networkError: false,
    _visibilityHandler: null,
    requestGeneration: 0,
    capped: false,
    map: null,
    enabled: false,
    soundEnabled: true,   // come v1: suono attivo di default (gate opzionale)
    features: [],         // buffer colpi {lat, lon, props, occurredMs}
    refT: null,           // istante timeline (null = live)
    statusHtml: '',
    _seenIds: new Set(),
    _audioCtx: null,
    _audioReady: false,
    _lastSoundAt: 0,
    _images: new Set(),
    _pulses: new Set(),
    _timer: null,
    _fetching: false,
    _pendingIdle: false,
};

// --- stile per età (port 1:1 di blitzAgeStyle, v1 ~2374) ---------------------
function blitzAgeStyle(ageMin) {
    if (ageMin <= 0.5)  return { color: '#fffbeb', stroke: '#fde047', glow: '#fef08a', opacity: 1.00, label: '< 30 s' };
    if (ageMin <= 2)    return { color: '#fde047', stroke: '#facc15', glow: '#fde047', opacity: 0.96, label: '< 2 min' };
    if (ageMin <= 5)    return { color: '#fbbf24', stroke: '#f59e0b', glow: '#fbbf24', opacity: 0.90, label: '2-5 min' };
    if (ageMin <= 12)   return { color: '#fb923c', stroke: '#ea580c', glow: '#fb923c', opacity: 0.80, label: '5-12 min' };
    if (ageMin <= 25)   return { color: '#f97316', stroke: '#c2410c', glow: '#f97316', opacity: 0.68, label: '12-25 min' };
    if (ageMin <= 40)   return { color: '#ef4444', stroke: '#991b1b', glow: '#ef4444', opacity: 0.54, label: '25-40 min' };
    return { color: '#b91c1c', stroke: '#7f1d1d', glow: '#b91c1c', opacity: 0.40, label: '> 40 min' };
}

function publishLightningLegend(on) {
    document.dispatchEvent(new CustomEvent('wsv2:map-legend', {
        detail: {
            id: 'nowcast-lightning',
            spec: on ? {
                title: 'Fulmini · Italia e isole',
                unit: 'eta colpo',
                steps: [
                    { color: blitzAgeStyle(0.4).color, label: '<30s' },
                    { color: blitzAgeStyle(1).color, label: '<2 min' },
                    { color: blitzAgeStyle(4).color, label: '2-5 min' },
                    { color: blitzAgeStyle(10).color, label: '5-12 min' },
                    { color: blitzAgeStyle(20).color, label: '12-25 min' },
                    { color: blitzAgeStyle(28).color, label: '25-30 min' },
                ],
            } : null,
        },
    }));
}

// Dimensione icona = n. stazioni (multiplicity) + confidenza (port 1:1, v1 ~2384).
function blitzIconSize(mult, confidence) {
    const n = Number(mult || 0);
    const c = Number(confidence || 0);
    const byStations = Number.isFinite(n) && n > 0 ? Math.sqrt(n) * 1.35 : 0;
    const byConfidence = Number.isFinite(c) && c > 0 ? Math.min(3, c / 30) : 0;
    return Math.round(Math.max(19, Math.min(30, 19 + byStations + byConfidence)));
}

// Timestamp occurred_at_utc → ms epoch (port 1:1 di blitzParseMs, v1 ~2500).
function blitzParseMs(occurredUtc) { return parseObservationTime(occurredUtc); }

// Età relativa leggibile (port di blitzRelLabel/blitzRelTime).
function blitzRelLabel(ageSec) {
    if (ageSec < 60) return `${Math.round(ageSec)} s fa`;
    if (ageSec < 3600) return `${Math.round(ageSec / 60)} min fa`;
    return `${Math.round(ageSec / 3600)} h fa`;
}

function esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// --- sprite canvas (DISEGNO identico a WSBlitzCanvasLayer._drawStrike, v1
//     ~1287-1314: aura radial gradient + nucleo cerchio con stroke + saetta
//     path scalato dalla viewBox 32×32 dell'SVG v1) --------------------------
function spriteDpr() {
    return Math.max(1, Math.min(window.devicePixelRatio || 1, 2.5));
}

function drawStrike(ctx, x, y, s) {
    const r = s.size / 2;
    ctx.globalAlpha = s.opacity;
    // aura (glow) — gradiente radiale
    const g = ctx.createRadialGradient(x, y, 0, x, y, r * 1.5);
    g.addColorStop(0, s.glow); g.addColorStop(0.5, s.glow); g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.globalAlpha = s.opacity * 0.55;
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(x, y, r * 1.5, 0, Math.PI * 2); ctx.fill();
    // nucleo
    ctx.globalAlpha = s.opacity;
    ctx.fillStyle = s.color;
    ctx.beginPath(); ctx.arc(x, y, r * 0.6, 0, Math.PI * 2); ctx.fill();
    ctx.lineWidth = 1.4; ctx.strokeStyle = s.stroke;
    ctx.stroke();
    // saetta (path scalato dalla viewBox 32×32 dell'SVG v1)
    const k = s.size / 32, ox = x - 16 * k, oy = y - 16 * k;
    ctx.fillStyle = s.stroke;
    ctx.beginPath();
    ctx.moveTo(ox + 17.4 * k, oy + 5.8 * k);
    ctx.lineTo(ox + 9.8 * k, oy + 17.2 * k);
    ctx.lineTo(ox + 15.1 * k, oy + 17.2 * k);
    ctx.lineTo(ox + 13.6 * k, oy + 26.4 * k);
    ctx.lineTo(ox + 22.4 * k, oy + 14.2 * k);
    ctx.lineTo(ox + 16.8 * k, oy + 14.2 * k);
    ctx.closePath(); ctx.fill();
    ctx.globalAlpha = 1;
}

// Sprite condiviso per combinazione size|color|glow|stroke (stessa chiave del
// motore GL v1). Opacity 1 nello sprite: quella vera (fade per età) è
// icon-opacity per-feature, così lo sprite resta condivisibile.
function ensureSprite(s) {
    const map = state.map;
    const key = `blz|${s.size}|${s.color}|${s.glow}|${s.stroke}`;
    if (map.hasImage(key)) return key;
    // Lato come nel motore GL v1 (side = size*1.5+4): contiene l'aura r*1.5.
    // Il disegno è al centro → canvas già simmetrico attorno all'anchor.
    const side = Math.ceil(s.size * 1.5) + 4;
    const dpr = spriteDpr();
    const c = document.createElement('canvas');
    c.width = Math.ceil(side * dpr);
    c.height = Math.ceil(side * dpr);
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawStrike(ctx, side / 2, side / 2, { ...s, opacity: 1 });
    map.addImage(key, ctx.getImageData(0, 0, c.width, c.height), { pixelRatio: dpr });
    state._images.add(key);
    return key;
}

function ensureLayer() {
    const map = state.map;
    if (map.getSource(SRC)) return;
    map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
        id: LYR,
        type: 'symbol',
        source: SRC,
        layout: {
            'icon-image': ['get', 'sk'],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
        },
        paint: {
            'icon-opacity': ['get', 'op'],
        },
    });
}

// --- suono (port 1:1 di unlockBlitzortungAudio/playBlitzortungSound) ---------
function unlockAudio() {
    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtor) return null;
    if (!state._audioCtx) state._audioCtx = new AudioCtor();
    if (state._audioCtx.state === 'suspended') {
        state._audioCtx.resume().catch(() => undefined);
    }
    return state._audioCtx;
}

function playSound(newCount) {
    if (!state.soundEnabled) return;
    const nowMs = Date.now();
    if (!newCount || document.hidden || nowMs - state._lastSoundAt < 2500) return;

    const ctx = unlockAudio();
    if (!ctx || ctx.state !== 'running') return;

    state._lastSoundAt = nowMs;
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.06, now + 0.012);  // 2026-07-03: alzato (era 0.035, poco udibile su telefono)
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.36);
    master.connect(ctx.destination);

    [760, 1120].forEach((freq, index) => {
        const start = now + index * 0.105;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, start);
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(index ? 0.55 : 0.7, start + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.13);
        osc.connect(gain);
        gain.connect(master);
        osc.start(start);
        osc.stop(start + 0.15);
    });
}

// --- pulse "nuovo fulmine": stesso markup/animazione CSS della v1 (divIcon
//     ws-blitz-pulse-icon) come maplibregl.Marker non interattivo, che si
//     auto-rimuove a fine animazione (ultimo anello: delay 0.44s + 1.6s).
function addPulse(lat, lon) {
    // Static strike symbols remain complete; only decorative DOM effects are bounded.
    if (document.hidden || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const mobile = window.matchMedia('(pointer: coarse)').matches;
    if (state._pulses.size >= (mobile ? 3 : 12)) return;
    if (state.map.isMoving() || !state.map.getBounds().contains([lon,lat])) return;
    const el = document.createElement('div');
    el.className = 'ws-blitz-pulse-icon';
    el.innerHTML = '<span class="ws-blitz-strike">'
        + '<span class="ws-blitz-flash"></span>'
        + '<span class="ws-blitz-ring"></span>'
        + '<span class="ws-blitz-ring ws-blitz-ring--2"></span>'
        + '<span class="ws-blitz-ring ws-blitz-ring--3"></span>'
        + '</span>';
    const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([lon, lat])
        .addTo(state.map);
    state._pulses.add(marker);
    const done = () => {
        if (!state._pulses.has(marker)) return;
        state._pulses.delete(marker);
        marker.remove();
    };
    const lastRing = el.querySelector('.ws-blitz-ring--3');
    if (lastRing) lastRing.addEventListener('animationend', done, { once: true });
    // Fallback (prefers-reduced-motion: animation none → nessun animationend).
    setTimeout(done, 2600);
}

function clearPulses() {
    state._pulses.forEach(m => m.remove());
    state._pulses.clear();
}

// --- render finestre (port 1:1 di renderBlitzWindow, v1 ~2515-2592) ----------
// Disegna i colpi del buffer caduti in [refMs - windowMin, refMs]. Colore = età
// relativa a refMs. Pulse "onda d'urto" + suono SOLO in modalità live.
function renderWindow(refMs, windowMin, live, fullMin) {
    const map = state.map;
    if (!map) return;
    if (!isMapStyleReady(map)) {
        if (!state._pendingIdle) {
            state._pendingIdle = true;
            map.once('idle', () => { state._pendingIdle = false; rerender(); });
        }
        return;
    }
    ensureLayer();

    const fromMs = refMs - windowMin * 60000;
    // Oltre fullMin (default = tutta la finestra) i colpi vengono mostrati
    // sbiaditi con opacità decrescente fino al bordo della finestra.
    const fadeStart = (typeof fullMin === 'number' && fullMin < windowMin) ? fullMin : windowMin;
    let freshCount = 0, newCount = 0, shown = 0, fadedCount = 0;
    const currentIds = new Set();
    const features = [];

    state.features.forEach(feat => {
        if (feat.occurredMs == null) return;
        if (feat.occurredMs < fromMs || feat.occurredMs > refMs + 1000) return;
        shown++;
        const ageSec = Math.max(0, (refMs - feat.occurredMs) / 1000);
        const ageMin = ageSec / 60;
        const age = blitzAgeStyle(ageMin);
        const p = feat.props;
        const isNew = live && p.id != null && !state._seenIds.has(p.id) && ageMin <= 2;
        if (p.id != null) currentIds.add(p.id);
        if (ageMin <= 1) freshCount++;
        if (isNew) newCount++;

        let markerOpacity = age.opacity;
        if (ageMin > fadeStart) {
            const t = Math.min(1, (ageMin - fadeStart) / Math.max(0.001, windowMin - fadeStart));
            markerOpacity = 0.55 - 0.42 * t; // 0.55 -> 0.13 (formula v1)
            fadedCount++;
        }

        const sk = ensureSprite({
            size: blitzIconSize(p.multiplicity, p.confidence),
            color: age.color, glow: age.glow, stroke: age.stroke,
        });
        features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [feat.lon, feat.lat] },
            properties: {
                sk,
                // Quantizzata a passi 0.05: pochi valori distinti = riuso.
                op: Math.round(markerOpacity * 20) / 20,
                lbl: blitzRelLabel(ageSec),
            },
        });

        if (isNew) addPulse(feat.lat, feat.lon);
    });

    map.getSource(SRC).setData({ type: 'FeatureCollection', features });

    if (live) {
        state._seenIds.clear();
        currentIds.forEach(id => state._seenIds.add(id));
        if (state._audioReady) playSound(newCount);
        state._audioReady = true;
    }

    state.statusHtml = live
        ? `<strong>Fulmini · Italia.</strong> ${shown.toLocaleString('it-IT')} negli ultimi ${windowMin}′`
            + (freshCount ? ` · ${freshCount.toLocaleString('it-IT')} nell'ultimo minuto` : '') + '.'
        : `<strong>Fulmini · Italia.</strong> ${shown.toLocaleString('it-IT')} rilevati nei ${WS_BLITZ_WINDOW_MIN}′ precedenti questo istante.`;
    if (state.upstreamStale) state.statusHtml += ' Rete in ritardo: vengono mostrate le ultime rilevazioni disponibili.';
    if (state.networkError) state.statusHtml += ' Aggiornamento non riuscito; riprovo automaticamente.';
    if (state.capped) state.statusHtml += ' Copertura incompleta della finestra: massimo 20.000 rilevazioni più recenti in Italia.';
    document.dispatchEvent(new CustomEvent('wsv2:lightning-status', { detail: {
        html: state.statusHtml, active: true, available: !state.networkError,
        stale: state.upstreamStale, capped: state.capped, count: shown,
        fetchedAtMs: state.fetchedAtMs, feedTimeMs: state.feedTimeMs,
        source: 'Blitzortung', coverage: 'Italia e isole', historical: !live,
    } }));
}

// Ridisegna all'istante corrente (live oppure lo scrub della timeline).
function rerender() {
    if (!state.enabled) return;
    if (state.refT != null) {
        renderWindow(state.refT, WS_BLITZ_WINDOW_MIN, false);
    } else {
        renderWindow(Date.now(), WS_BLITZ_LIVE_FADE_MIN, true, WS_BLITZ_LIVE_FULL_MIN);
    }
}

// --- fetch (port di loadBlitzortungLayer, v1 ~2608) ---------------------------
async function loadData() {
    if (state._fetching) return;
    state._fetching = true;
    const generation = state.requestGeneration;
    try {
        // National acquisition is independent of camera, zoom and satellite area.
        const resp = await fetch(
            `${WS_LIGHTNING_API}?resource=detections&provider=blitzortung&quality=all&area=italy&since_minutes=${WS_BLITZ_MAX_AGE_MIN}&limit=20000`,
            { cache: 'no-store', signal: AbortSignal.timeout(12000) });
        const data = await resp.json();
        if (!resp.ok || data.type !== 'FeatureCollection' || !Array.isArray(data.features)) throw new Error('BLITZORTUNG_ERROR');
        if (!state.enabled || generation !== state.requestGeneration) return;
        const meta = { ...(data.metadata || {}), ...(data.meta || {}),
            freshness: { ...(data.metadata?.freshness || {}), ...(data.meta?.freshness || {}) } };
        state.capped = meta.capped === true;
        state.upstreamStale = meta.upstream_fresh === false;
        state.networkError = false;
        state.fetchedAtMs = Date.now();
        state.feedTimeMs = parseObservationTime(meta.latest_observation_at_utc || meta.latest_observation_utc || meta.latest_observed_at_utc
            || meta.upstream_last_frame_utc || meta.freshness?.latest_observation_at_utc || meta.freshness?.latest_observation_utc);
        state.features = data.features.map(f => {
            const c = f.geometry?.coordinates;
            const p = f.properties || {};
            const occurredMs = blitzParseMs(p.occurred_at_utc);
            if (f.geometry?.type !== 'Point' || !Array.isArray(c) || !c.slice(0, 2).every(Number.isFinite) || occurredMs === null) return null;
            return { lat: c[1], lon: c[0], props: p, occurredMs };
        }).filter(Boolean);
        rerender();
        if (NowcastTimeline.isActive()) NowcastTimeline.notifyDataUpdated();
    } catch (err) {
        if (!state.enabled || generation !== state.requestGeneration) return;
        console.warn('[wsv2] fulmini: fetch fallita', err);
        state.networkError = true;
        // Age filtering continues during outages; old strikes cannot remain live.
        rerender();
    } finally {
        state._fetching = false;
        if (state.enabled && generation !== state.requestGeneration) loadData();
    }
}

// Retained for integrations that used the old area selector. National coverage
// is a product invariant; this setting cannot clip Italy after a camera change.
export function setLightningArea() { state.area = 'italy'; }

// --- tooltip hover (pattern popup di stations.js; testo = blitzTooltip v1) ---
let hoverPopup = null;
function wireInteractions() {
    const map = state.map;
    hoverPopup = new maplibregl.Popup({
        closeButton: false, closeOnClick: false, offset: 12,
        className: 'wsv2-tooltip-shell', maxWidth: '220px',
    });
    map.on('mousemove', LYR, (e) => {
        const f = e.features && e.features[0];
        if (!f) return;
        map.getCanvas().style.cursor = 'pointer';
        const [lon, lat] = f.geometry.coordinates;
        const html = `<div class="ws-lightning-tooltip"><strong>Fulmine</strong><span>Caduto ${esc(f.properties.lbl)}</span></div>`;
        hoverPopup.setLngLat([lon, lat]).setHTML(html).addTo(map);
    });
    map.on('mouseleave', LYR, () => {
        map.getCanvas().style.cursor = '';
        hoverPopup.remove();
    });
}

// --- API pubblica -------------------------------------------------------------
export function initLightning(map, options) {
    state.map = map;
    if (options && typeof options.soundEnabled === 'boolean') {
        state.soundEnabled = options.soundEnabled;
    }
    wireInteractions();
    // Sblocco audio al primo gesto utente (come v1).
    ['pointerdown', 'keydown', 'touchstart'].forEach(eventName => {
        document.addEventListener(eventName, unlockAudio, { once: true, passive: true });
    });
}

// Attiva/disattiva layer + auto-refresh (port di activate/deactivateBlitzortungLayer).
export function setEnabled(enabled) {
    if (!!enabled === state.enabled) return;
    state.enabled = !!enabled;
    state.requestGeneration++;
    if (state.enabled) {
        publishLightningLegend(true);
        state._seenIds.clear();
        state._audioReady = false;
        document.dispatchEvent(new CustomEvent('wsv2:lightning-status', { detail: { active: true, loading: true } }));
        unlockAudio();
        loadData();
        NowcastTimeline.register('lightning', showAtTime);
        state._visibilityHandler = () => { if (!document.hidden && state.enabled) loadData(); };
        document.addEventListener('visibilitychange', state._visibilityHandler);
        state._timer = setInterval(() => {
            if (document.hidden) return;
            if (state.enabled) loadData();
        }, WS_BLITZ_REFRESH_MS);
    } else {
        NowcastTimeline.unregister('lightning');
        if (state._visibilityHandler) document.removeEventListener('visibilitychange', state._visibilityHandler);
        state._visibilityHandler = null;
        if (state._timer) clearInterval(state._timer);
        state._timer = null;
        clearPulses();
        if (hoverPopup) hoverPopup.remove();
        state.features = [];
        state.refT = null;
        state._seenIds.clear();
        state._audioReady = false;
        const src = state.map && state.map.getSource(SRC);
        if (src) src.setData({ type: 'FeatureCollection', features: [] });
        state.statusHtml = '';
        publishLightningLegend(false);
        document.dispatchEvent(new CustomEvent('wsv2:lightning-status', { detail: { html: '', active: false } }));
    }
}

// Guidato dalla timeline nowcast (port 1:1 di blitzShowAtTime, v1 ~2598):
// a ORA (entro 90s) vista live completa; nel passato solo la finestra 5′.
export function showAtTime(T) {
    const now = Date.now();
    if (Math.abs(T - NowcastTimeline.referenceTime()) < 1000 || Math.abs(T - now) <= WS_BLITZ_LIVE_EPSILON_MS) {
        state.refT = null;
        renderWindow(now, WS_BLITZ_LIVE_FADE_MIN, true, WS_BLITZ_LIVE_FULL_MIN);
    } else {
        state.refT = T;
        renderWindow(T, WS_BLITZ_WINDOW_MIN, false);
    }
}
